import _ from 'lodash';
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/Button';
import FMPTextField from '../ui/FMPTextField';
import FMPTextFieldNoGrid from '../ui/FMPTextFieldNoGrid';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';

import { withStyles } from '@material-ui/core/styles/index';

const MHX_TERMINAL_ID = 31;
const IECFT_TERMINAL_ID = 56;

const styles = () => {
	return {
		addInputButton: {
			borderRadius: '100px',
			marginTop: 20,
		},
		supplierDetail: {
			margin: 5,
			color: '#999',
			fontSize: 13,
			fontStyle: 'italic',
		},
	};
};

const formFields = {
	backups: [
		{
			supplierId: '',
			loadingNumber: '',
			accountNumber: '',
		},
	],
};

function generateMhxLoadingNumber() {
	return Math.floor(100000 + Math.random() * 900000).toString();
}

function generateIecftLoadingNumber(stationNumberRaw) {
	const stationNumber = (stationNumberRaw || '').toString().trim().toUpperCase();
	if (!stationNumber) {
		return '';
	}

	// Starts with GM -> 222XXX (GM005 -> 222005)
	if (stationNumber.startsWith('GM')) {
		const digits = stationNumber.replace(/^GM/i, '').replace(/\D/g, '');
		const last3 = digits.padStart(3, '0').slice(-3);
		return `222${last3}`;
	}

	// Starts with R -> 333XXX (R024 -> 333024)
	if (stationNumber.startsWith('R')) {
		const digits = stationNumber.replace(/^R/i, '').replace(/\D/g, '');
		const last3 = digits.padStart(3, '0').slice(-3);
		return `333${last3}`;
	}

	// Starts with number 2 -> 44 + stationNumber digits (2004 -> 4442004)
	if (stationNumber.startsWith('2')) {
		const digits = stationNumber.replace(/\D/g, '');
		if (!digits) {
			return '';
		}
		return `44${digits}`;
	}

	return '';
}

class DeliveryTerminalSupplierPicker extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			terminalId: '',
			supplierId: '',
			loadingNumber: '',
			secondaryLoadingNumber: '',
			stationNumber: '',
			secondaryStationNumber: '',
			accountNumber: '',
			emailCopyOfDeliveryRequest: '',
			useStationDeliveryIdAsLoadingNumber: '',
			requireLoadingNumberPerStationOnSplitLoads: '',
			useLoadingAssignments: '',
			carrierNeedsToProvidePickupWindow: '',
			backups: [],
			supplierOptions: [],
		};
	}

	componentDidUpdate(prevProps) {
		const curProps = this.props;
		const { terminalId, secondaryLoadingNumber } = this.state;

		console.log(
			'CALLING COM DID UPATE',
			curProps.isSplitLoad,
			prevProps.isSplitLoad,
			secondaryLoadingNumber
		);

		if (curProps.isSplitLoad && prevProps.isSplitLoad !== curProps.isSplitLoad) {
			if (terminalId === MHX_TERMINAL_ID && !secondaryLoadingNumber) {
				const newState = {
					secondaryLoadingNumber: generateMhxLoadingNumber(),
				};
				this.setState(newState);
				this.callOnChange(newState);
			}

			if (terminalId === IECFT_TERMINAL_ID && !secondaryLoadingNumber) {
				const secondaryStationNumber = curProps.secondaryStationNumber || curProps.stationNumber;
				const newState = {
					secondaryLoadingNumber: generateIecftLoadingNumber(secondaryStationNumber),
				};
				this.setState(newState);
				this.callOnChange(newState);
			}
		} else if (!curProps.isSplitLoad && prevProps.isSplitLoad !== curProps.isSplitLoad) {
			if (secondaryLoadingNumber) {
				const newState = {
					secondaryLoadingNumber: '',
				};
				this.setState(newState);
				this.callOnChange(newState);
			}
		}
	}

	refreshGeneratedLoadingNumber = (fieldName) => {
		const { terminalId } = this.state;
		let nextValue = '';
		if (terminalId === MHX_TERMINAL_ID) {
			nextValue = generateMhxLoadingNumber();
		} else if (terminalId === IECFT_TERMINAL_ID) {
			const stationNumberRaw =
				fieldName === 'secondaryLoadingNumber'
					? this.props.secondaryStationNumber || this.props.stationNumber
					: this.props.stationNumber;
			nextValue = generateIecftLoadingNumber(stationNumberRaw);
		}

		const newState = { [fieldName]: nextValue };
		this.setState(newState);
		this.callOnChange(newState);
	};

	// noinspection JSUnusedGlobalSymbols
	static getDerivedStateFromProps(nextProps, prevState) {
		const { terminals, supplierMap, productTypes, onChange } = nextProps;
		const {
			terminalId,
			supplierId,
			isSplitLoad,
			stationNumber,
			secondaryStationNumber,
			accountNumber,
			emailCopyOfDeliveryRequest,
			useStationDeliveryIdAsLoadingNumber,
			carrierNeedsToProvidePickupWindow,
			requireLoadingNumberPerStationOnSplitLoads,
			useLoadingAssignments,
			backups,
		} = nextProps;

		let { loadingNumber, secondaryLoadingNumber } = nextProps;

		//- No backups? Clear them
		let newState = {};

		if (
			terminalId === prevState.terminalId &&
			supplierId === prevState.supplierId &&
			backups === prevState.backups &&
			stationNumber === prevState.stationNumber &&
			secondaryStationNumber === prevState.secondaryStationNumber
		) {
			return newState;
		}

		if (!terminalId) {
			newState.terminalId = '';
			return newState;
		}

		const stateLoadingNumber = prevState.loadingNumber;
		const stateSecondaryLoadingNumber = prevState.secondaryLoadingNumber;
		if (terminalId !== prevState.terminalId && terminalId === MHX_TERMINAL_ID && !loadingNumber) {
			loadingNumber = generateMhxLoadingNumber();
			if (isSplitLoad) {
				secondaryLoadingNumber = generateMhxLoadingNumber();
			}
		}

		if (
			terminalId === IECFT_TERMINAL_ID &&
			!loadingNumber &&
			(terminalId !== prevState.terminalId || stationNumber !== prevState.stationNumber)
		) {
			loadingNumber = generateIecftLoadingNumber(stationNumber);
			if (isSplitLoad) {
				secondaryLoadingNumber = generateIecftLoadingNumber(secondaryStationNumber || stationNumber);
			}
		}

		if (
			isSplitLoad &&
			isSplitLoad !== prevState.isSplitLoad &&
			terminalId === MHX_TERMINAL_ID &&
			!secondaryLoadingNumber
		) {
			secondaryLoadingNumber = generateMhxLoadingNumber();
		}

		if (
			isSplitLoad &&
			isSplitLoad !== prevState.isSplitLoad &&
			terminalId === IECFT_TERMINAL_ID &&
			!secondaryLoadingNumber
		) {
			secondaryLoadingNumber = generateIecftLoadingNumber(secondaryStationNumber || stationNumber);
		}

		if (
			terminalId === IECFT_TERMINAL_ID &&
			isSplitLoad &&
			!secondaryLoadingNumber &&
			secondaryStationNumber !== prevState.secondaryStationNumber
		) {
			secondaryLoadingNumber = generateIecftLoadingNumber(secondaryStationNumber || stationNumber);
		}

		let isLoadingNumberGenerated = false;
		if (terminalId === MHX_TERMINAL_ID || terminalId === IECFT_TERMINAL_ID) {
			isLoadingNumberGenerated = true;
		}

		//- Either we just mounted or we got a new prop passed in
		//- so lets reset the state
		newState = {
			terminalId,
			supplierId: '',
			//loadingNumber,
			loadingNumber: stateLoadingNumber || loadingNumber,
			secondaryLoadingNumber: stateSecondaryLoadingNumber || secondaryLoadingNumber,
			stationNumber,
			secondaryStationNumber,
			isLoadingNumberGenerated,
			accountNumber: '',
			emailCopyOfDeliveryRequest: false,
			useStationDeliveryIdAsLoadingNumber: false,
			carrierNeedsToProvidePickupWindow: false,
			backups: [],
			supplierOptions: [],
		};
		const terminal = _.find(terminals, { terminalId });
		if (!terminal) {
			console.error('Missing terminal', terminals, terminalId);
			return;
		}

		const supplierOptions = getSupplierOptions({
			terminal,
			supplierMap,
			productTypes,
		});
		newState.supplierOptions = supplierOptions;

		//- Populate loading / account numbers
		if (supplierId) {
			newState.supplierId = supplierId;
			const supplierData = _.find(supplierOptions, { supplierId });

			if (supplierData) {
				newState.loadingNumber = supplierData.loadingNumber;
				newState.secondaryLoadingNumber = supplierData.secondaryLoadingNumber;
				newState.accountNumber = supplierData.accountNumber;
				newState.emailCopyOfDeliveryRequest = supplierData.emailCopyOfDeliveryRequest;
				newState.useStationDeliveryIdAsLoadingNumber =
					supplierData.useStationDeliveryIdAsLoadingNumber;
				newState.requireLoadingNumberPerStationOnSplitLoads =
					supplierData.requireLoadingNumberPerStationOnSplitLoads;
				newState.useLoadingAssignments = supplierData.useLoadingAssignments;
				newState.carrierNeedsToProvidePickupWindow = supplierData.carrierNeedsToProvidePickupWindow;

				if (supplierData.useStationDeliveryIdAsLoadingNumber) {
					newState.loadingNumber = '';
					newState.secondaryLoadingNumber = '';
				}
			}
		}

		if (loadingNumber) {
			newState.loadingNumber = loadingNumber;
		}
		if (secondaryLoadingNumber) {
			newState.secondaryLoadingNumber = secondaryLoadingNumber;
		}

		if (accountNumber) {
			newState.accountNumber = accountNumber;
		}

		if (emailCopyOfDeliveryRequest) {
			newState.emailCopyOfDeliveryRequest = emailCopyOfDeliveryRequest;
		}

		if (useStationDeliveryIdAsLoadingNumber) {
			newState.useStationDeliveryIdAsLoadingNumber = useStationDeliveryIdAsLoadingNumber;
		}
		if (requireLoadingNumberPerStationOnSplitLoads) {
			newState.requireLoadingNumberPerStationOnSplitLoads = requireLoadingNumberPerStationOnSplitLoads;
		}

		if (useLoadingAssignments) {
			newState.useLoadingAssignments = useLoadingAssignments;
		}

		if (carrierNeedsToProvidePickupWindow) {
			newState.carrierNeedsToProvidePickupWindow = carrierNeedsToProvidePickupWindow;
		}

		//- Populate loading / account numbers PER backup
		if (backups && backups.length) {
			//backups.forEach(backup => {
			//	const supplierId = backup.supplierId;
			//	const supplierData = _.find(supplierOptions, { supplierId });
			//	backup.loadingNumber = supplierData.loadingNumber;
			//	backup.accountNumber = supplierData.accountNumber;
			//});
			newState.backups = backups;
		}

		onChange(newState);
		return newState;
	}

	onSupplierChange = (event, index) => {
		const { stationDeliveryId } = this.props;
		const supplierId = event.target.value;
		const { supplierOptions } = this.state;
		const option = _.find(supplierOptions, { supplierId });

		// Keep existing loading number
		const stateLoadingNumber = this.state.loadingNumber;
		const stateSecondaryLoadingNumber = this.state.secondaryLoadingNumber;
		const {
			loadingNumber = '',
			secondaryLoadingNumber = '',
			accountNumber = '',
			emailCopyOfDeliveryRequest = false,
			useStationDeliveryIdAsLoadingNumber = false,
			requireLoadingNumberPerStationOnSplitLoads = false,
			useLoadingAssignments = false,
			carrierNeedsToProvidePickupWindow = false,
		} = option;

		let newState = {};
		if (!_.isNumber(index)) {
			newState = {
				supplierId,
				loadingNumber: stateLoadingNumber || loadingNumber,
				secondaryLoadingNumber: stateSecondaryLoadingNumber || secondaryLoadingNumber,
				accountNumber,
				emailCopyOfDeliveryRequest,
				useStationDeliveryIdAsLoadingNumber,
				requireLoadingNumberPerStationOnSplitLoads,
				useLoadingAssignments,
				carrierNeedsToProvidePickupWindow,
			};
			if (useStationDeliveryIdAsLoadingNumber && stationDeliveryId) {
				newState.loadingNumber = stationDeliveryId;
				newState.secondaryLoadingNumber = stationDeliveryId;
			}
		} else {
			const backups = this.state.backups.slice();
			backups[index].supplierId = supplierId;
			backups[index].loadingNumber = loadingNumber;
			backups[index].accountNumber = accountNumber;
			newState.backups = backups;
		}
		this.callOnChange(newState);
	};

	onTerminalChange = (event) => {
		const terminalId = event.target.value;
		const { terminals, supplierMap, productTypes, isSplitLoad } = this.props;
		const terminal = _.find(terminals, { terminalId });
		const supplierOptions = getSupplierOptions({
			terminal,
			supplierMap,
			productTypes,
		});

		let loadingNumber = '';
		let secondaryLoadingNumber = '';
		let isLoadingNumberGenerated = false;
		if (terminalId === MHX_TERMINAL_ID) {
			isLoadingNumberGenerated = true;
			loadingNumber = generateMhxLoadingNumber();
			if (isSplitLoad) {
				secondaryLoadingNumber = generateMhxLoadingNumber();
			}
		}
		if (terminalId === IECFT_TERMINAL_ID) {
			isLoadingNumberGenerated = true;
			loadingNumber = generateIecftLoadingNumber(this.props.stationNumber);
			if (isSplitLoad) {
				secondaryLoadingNumber = generateIecftLoadingNumber(
					this.props.secondaryStationNumber || this.props.stationNumber
				);
			}
		}

		const newState = {
			terminalId,
			supplierOptions,
			supplierId: '',
			loadingNumber,
			secondaryLoadingNumber,
			stationNumber: this.props.stationNumber,
			secondaryStationNumber: this.props.secondaryStationNumber,
			accountNumber: '',
			emailCopyOfDeliveryRequest: false,
			useStationDeliveryIdAsLoadingNumber: false,
			carrierNeedsToProvidePickupWindow: false,
			requireLoadingNumberPerStationOnSplitLoads: false,
			useLoadingAssignments: false,
			backups: [],
			isLoadingNumberGenerated,
		};
		this.callOnChange(newState);
	};

	onChange(event, fieldName, index, attr) {
		const newState = {};
		if (!_.isNumber(index)) {
			newState[fieldName] = event.target.value;
		} else {
			const inputs = this.state[fieldName].slice();
			if (attr) {
				inputs[index][attr] = event.target.value;
			} else {
				inputs[index] = event.target.value;
			}
			newState[fieldName] = inputs;
		}
		this.callOnChange(newState);
	}

	callOnChange = (newState) => {
		this.props.onChange(newState);
		this.setState(newState);
	};

	addInput = (arrayName) => {
		const val = _.cloneDeep(formFields[arrayName]);
		const newState = {
			[arrayName]: this.state[arrayName].concat(val),
		};
		this.callOnChange(newState);
	};

	removeInput = (arrayName, index) => {
		const inputs = this.state[arrayName].slice();
		inputs.splice(index, 1);
		const newState = { [arrayName]: inputs };
		this.callOnChange(newState);
	};

	render() {
		const {
			error,
			label,
			terminals,
			classes,
			isSplitLoad,
			productTypes,
			stationDeliveryId,
		} = this.props;
		const {
			terminalId,
			supplierId,
			emailCopyOfDeliveryRequest,
			useStationDeliveryIdAsLoadingNumber,
			carrierNeedsToProvidePickupWindow,
			requireLoadingNumberPerStationOnSplitLoads,
			useLoadingAssignments,
			backups,
			supplierOptions,
			isLoadingNumberGenerated,
		} = this.state;

		const disabled = !supplierOptions || !supplierOptions.length;

		let showTwo = false;
		if (isSplitLoad && requireLoadingNumberPerStationOnSplitLoads) {
			showTwo = requireLoadingNumberPerStationOnSplitLoads;
		}

		let loadingHelpText = '';
		if (useStationDeliveryIdAsLoadingNumber) {
			loadingHelpText = `✔ Use Pearson Delivery ID`;
		}

		let loadingNumberDisabled = disabled;

		if (useLoadingAssignments) {
			loadingHelpText = `✔ Use next available FMP loading number`;

			if (!stationDeliveryId) {
				loadingNumberDisabled = true;
			}
		}

		if (isLoadingNumberGenerated) {
			if (terminalId === MHX_TERMINAL_ID) {
				loadingHelpText = `✔ Generated automatically for MHX`;
			} else if (terminalId === IECFT_TERMINAL_ID) {
				loadingHelpText = `✔ Generated from station # for IECFT`;
			} else {
				loadingHelpText = `✔ Generated automatically`;
			}
		}

		//stationDeliveryId means its an update
		if (useStationDeliveryIdAsLoadingNumber && !stationDeliveryId) {
			loadingNumberDisabled = true;
		}

		const productTypeMap = _.keyBy(productTypes);
		let terminalsWithProduct = _.filter(terminals, (terminal) => {
			if (!terminal.supplierProducts) {
				return false;
			}
			return _.some(terminal.supplierProducts, (sp) => {
				return productTypeMap[sp.product];
			});
		});

		terminalsWithProduct = _.sortBy(terminalsWithProduct, 'name');
		return (
			<div>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<FormControl fullWidth={true} error={!!error}>
							<InputLabel>{label}</InputLabel>
							<Select
								value={terminalId}
								onChange={(event) => this.onTerminalChange(event)}
								input={<Input />}
							>
								{terminalsWithProduct.map((terminal) => {
									const { terminalId, name } = terminal;
									return (
										<MenuItem key={terminalId} value={terminalId}>
											{name}
										</MenuItem>
									);
								})}
							</Select>
							{error && <FormHelperText>{error}</FormHelperText>}
						</FormControl>
					</Grid>
				</Grid>

				<Grid container spacing={2}>
					<Grid item xs={12} md={4}>
						{this.renderSupplierOptions({
							supplierOptions,
							label: 'Primary Supplier',
							supplierId,
							disabled,
						})}

						{emailCopyOfDeliveryRequest && (
							<div className={classes.supplierDetail}>
								<span style={{ opacity: 0.8 }}>✔</span>️ Supplier will receive fuel pickup email
							</div>
						)}

						{carrierNeedsToProvidePickupWindow && (
							<div className={classes.supplierDetail}>
								<span style={{ opacity: 0.8 }}>✔</span>️ Carrier will enter pickup window
							</div>
						)}

						{showTwo && (
							<div className={classes.supplierDetail}>
								<span style={{ opacity: 0.8 }}>✔</span>️ Require two loading #s on split load
							</div>
						)}
					</Grid>

					{showTwo && (
						<Fragment>
							<Grid item xs={12} md={4} style={{ display: 'flex' }}>
								<FMPTextFieldNoGrid
									name={'loadingNumber'}
									label={'Station 1 Loading Number'}
									controller={this}
									disabled={loadingNumberDisabled}
									contained={false}
									helpText={loadingHelpText}
								/>
								{isLoadingNumberGenerated && (
									<IconButton
										aria-label="refresh loading #"
										onClick={() => this.refreshGeneratedLoadingNumber('loadingNumber')}
										disabled={loadingNumberDisabled}
									>
										<RefreshIcon />
									</IconButton>
								)}
							</Grid>

							<Grid item xs={12} md={4} style={{ display: 'flex' }}>
								<FMPTextField
									name={'secondaryLoadingNumber'}
									label={'Station 2 Loading Number'}
									controller={this}
									disabled={loadingNumberDisabled}
									contained={false}
									helpText={loadingHelpText}
								/>

								{isLoadingNumberGenerated && (
									<IconButton
										aria-label="refresh loading #"
										onClick={() => this.refreshGeneratedLoadingNumber('secondaryLoadingNumber')}
										disabled={loadingNumberDisabled}
									>
										<RefreshIcon />
									</IconButton>
								)}
							</Grid>
						</Fragment>
					)}

					{!showTwo && (
						<Fragment>
							<Grid item xs={12} md={4} style={{ display: 'flex' }}>
								<FMPTextFieldNoGrid
									name={'loadingNumber'}
									label={'Loading Number'}
									controller={this}
									disabled={loadingNumberDisabled}
									contained={false}
									helpText={loadingHelpText}
								/>

								{isLoadingNumberGenerated && (
									<IconButton
										aria-label="refresh loading #"
										onClick={() => this.refreshGeneratedLoadingNumber('loadingNumber')}
										disabled={loadingNumberDisabled}
									>
										<RefreshIcon />
									</IconButton>
								)}
							</Grid>

							<FMPTextField
								name={'accountNumber'}
								label={'Account Number'}
								controller={this}
								md={3}
								disabled={disabled}
								contained={false}
							/>
						</Fragment>
					)}
				</Grid>

				{backups &&
					backups.map((backup, i) => {
						return (
							<Grid container spacing={2} key={i}>
								<Grid item xs={12} md={5}>
									{this.renderSupplierOptions({
										supplierOptions,
										label: 'Backup Supplier',
										supplierId: backup.supplierId,
										index: i,
										disabled,
									})}
								</Grid>

								<FMPTextField
									name={'backups'}
									index={i}
									attr={'loadingNumber'}
									label={'Loading Number'}
									controller={this}
									md={3}
									disabled={disabled}
									contained={false}
								/>

								<FMPTextField
									name={'backups'}
									index={i}
									attr={'accountNumber'}
									label={'Account Number'}
									controller={this}
									md={3}
									disabled={disabled}
									contained={false}
								/>

								<Grid item xs={12} md={1}>
									<IconButton
										color="primary"
										aria-label="Delete"
										onClick={() => this.removeInput('backups', i)}
									>
										<DeleteIcon />
									</IconButton>
								</Grid>
							</Grid>
						);
					})}

				<Grid container spacing={2}>
					<Grid item xs={12} md={5}>
						<Button
							disabled={disabled}
							variant="contained"
							className={classes.addInputButton}
							onClick={() => this.addInput('backups')}
						>
							ADD A BACKUP SUPPLIER
						</Button>
					</Grid>
				</Grid>
			</div>
		);
	}

	renderSupplierOptions({ supplierOptions, supplierId, index, disabled, label }) {
		const styles = {
			product: {
				paddingLeft: 10,
				fontSize: 14,
				color: '#666',
			},
			loading: {
				fontSize: 11,
				lineHeight: '11px',
				color: '#777',
			},
		};

		return (
			<FormControl fullWidth={true} disabled={disabled}>
				<InputLabel>{label}</InputLabel>
				<Select
					value={supplierId}
					onChange={(event) => this.onSupplierChange(event, index)}
					input={<Input />}
				>
					{supplierOptions.map((option) => {
						const { supplierId, name, product } = option;
						return (
							<MenuItem key={supplierId} value={supplierId}>
								<span>{name}</span>
								<span style={styles.product}>{product}</span>
							</MenuItem>
						);
					})}
				</Select>
			</FormControl>
		);
	}
}

function getSupplierOptions({ terminal, supplierMap, productTypes }) {
	const productTypeMap = _.keyBy(productTypes);
	const supplierOptions = [];
	terminal.supplierProducts.forEach((sp) => {
		const { supplierId, product } = sp;
		const supplier = supplierMap[supplierId];
		sp.name = supplier.name;

		if (!supplier) {
			return console.warn('Missing supplier', sp);
		}
		if (!productTypeMap[product]) {
			return;
		}
		supplierOptions.push(sp);
	});
	return supplierOptions;
}

DeliveryTerminalSupplierPicker.propTypes = {
	label: PropTypes.string,
	error: PropTypes.string,
	isSplitLoad: PropTypes.bool,
	terminals: PropTypes.array,
	supplierMap: PropTypes.object,
	productTypes: PropTypes.array,
	terminalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	supplierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	loadingNumber: PropTypes.string,
	secondaryLoadingNumber: PropTypes.string,
	stationNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	secondaryStationNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	accountNumber: PropTypes.string,
	emailCopyOfDeliveryRequest: PropTypes.bool,
	useStationDeliveryIdAsLoadingNumber: PropTypes.bool,
	carrierNeedsToProvidePickupWindow: PropTypes.bool,
	requireLoadingNumberPerStationOnSplitLoads: PropTypes.bool,
	useLoadingAssignments: PropTypes.bool,
	backups: PropTypes.array,
	onChange: PropTypes.func,

	stationDeliveryId: PropTypes.number,
	classes: PropTypes.object,
};

export default _.flow([withStyles(styles)])(DeliveryTerminalSupplierPicker);
